import { AllocationEntitiesTableProps } from "features/allocations/AllocationEntitiesTable";
import {
  useAllocationRuleInvestingEntitiesRetrieveSuspense,
  useAllocationRuleRetrieveSuspense,
} from "gen/endpoints/allocation-rule/allocation-rule";
import { useEntityAllocationRulesRetrieveSuspense } from "gen/endpoints/allocation-rule-lookup/allocation-rule-lookup";
import { useCommitmentInformationListSuspense } from "gen/endpoints/commitment-information/commitment-information";
import { AllocationRuleEdgeListUpsertDataRequest } from "gen/model/allocationRuleEdgeListUpsertDataRequest";
import { mapValues } from "lodash-es";
import { useCallback, useMemo } from "react";
import { FieldPath, useForm, UseFormReturn, useWatch } from "react-hook-form";
import {
  OnChangeFn,
  RowSelectionState,
} from "shared/components/table/data/DataTableTypes";

type AllocationSetupFormInvestorsData = {
  [id: string]: {
    selected: boolean;
  };
};

type AllocationSetupFormEntitiesData = {
  [id: string]: {
    selected: boolean;
    rule_id: string;
  };
};

export interface AllocationSetupFormData {
  investors: AllocationSetupFormInvestorsData;
  entities: AllocationSetupFormEntitiesData;
}

type HasSelected = { selected: boolean | undefined };

const filterSelected = <T extends HasSelected>(
  item: [string, T],
): item is [string, T] & [string, { selected: true }] =>
  item[1].selected === true;

const selectedEntries = <T extends HasSelected>(
  obj: { [key: string]: T } | undefined,
) => Object.entries(obj ?? {}).filter(filterSelected);

const getSelectedRows = (investorsRowSelectionState: {
  [key: string]: boolean;
}) =>
  Object.entries(investorsRowSelectionState).filter(([, selected]) => selected);

const makeRowSelectionState = (
  obj: { [key: string]: HasSelected } | undefined,
) => mapValues(obj, ({ selected }) => selected === true);

const makeOnRowSelectionChanged =
  (
    form: UseFormReturn<AllocationSetupFormData>,
    fieldName: FieldPath<AllocationSetupFormData>,
    values: Record<string, object>,
    // eslint-disable-next-line no-undef
    rowSelectionState: RowSelectionState,
  ): OnChangeFn<RowSelectionState> =>
  (updaterOrValue) => {
    const newSelectionState =
      typeof updaterOrValue === "function"
        ? updaterOrValue(rowSelectionState)
        : updaterOrValue;
    const allIds = new Set([
      ...Object.keys(values),
      ...Object.keys(newSelectionState),
    ]);
    form.setValue(
      fieldName,
      [...allIds].reduce(
        (acc, id) => ({
          ...acc,
          [id]: {
            ...values[id],
            selected: newSelectionState[id] ?? false,
          },
        }),
        {},
      ),
      { shouldDirty: true },
    );
  };

export const useAllocationSetupForm = (
  fundFamilyId: string,
  allocationRuleId: string,
) => {
  const { data: allocationRuleData } = useAllocationRuleRetrieveSuspense(
    fundFamilyId,
    allocationRuleId,
  );

  const { data: currentInvestingEntityData } =
    useAllocationRuleInvestingEntitiesRetrieveSuspense(
      fundFamilyId,
      allocationRuleId,
    );

  const { data: entityAllocationsData } =
    useEntityAllocationRulesRetrieveSuspense(fundFamilyId, {
      entity_id: allocationRuleData.entity_id,
    });

  const { data: commitmentData } = useCommitmentInformationListSuspense(
    fundFamilyId,
    {
      fund_id: allocationRuleData.entity_id,
    },
  );

  const defaultValues = (): Promise<AllocationSetupFormData> => {
    const ruleIdForEntityIdMap = new Map(
      currentInvestingEntityData.entities.map((entity) => [
        entity.entity_id,
        entity.rule_id,
      ]),
    );

    const selectedSecurityOwnershipIds = new Set(
      currentInvestingEntityData.commitments.rows.map(
        (commitment) => commitment.id,
      ),
    );

    const investors = commitmentData.reduce<AllocationSetupFormInvestorsData>(
      (acc, commitment) => {
        acc[commitment.id] = {
          selected: selectedSecurityOwnershipIds.has(commitment.id),
        };
        return acc;
      },
      {},
    );

    const entities =
      entityAllocationsData.reduce<AllocationSetupFormEntitiesData>(
        (acc, entity) => {
          acc[entity.entity_id] = {
            selected: ruleIdForEntityIdMap.has(entity.entity_id),
            rule_id: ruleIdForEntityIdMap.get(entity.entity_id) ?? "",
          };
          return acc;
        },
        {},
      );

    return Promise.resolve({
      investors,
      entities,
    });
  };

  const form = useForm<AllocationSetupFormData>({
    defaultValues,
  });

  // This causes a re-render when the form changes, however form.getValues() returns
  // more recent values than the return value of this hook.
  useWatch(form);
  const values = form.getValues();

  const investorsRowSelectionState = useMemo(
    () => makeRowSelectionState(values.investors),
    [values.investors],
  );
  const entitiesRowSelectionState = useMemo(
    () => makeRowSelectionState(values.entities),
    [values.entities],
  );

  const numSelectedInvestors = getSelectedRows(
    investorsRowSelectionState,
  ).length;
  const numSelectedEntities = getSelectedRows(entitiesRowSelectionState).length;

  const onEntitiesRowSelectionChanged = useMemo(
    () =>
      makeOnRowSelectionChanged(
        form,
        "entities",
        values.entities,
        entitiesRowSelectionState,
      ),
    [entitiesRowSelectionState, form, values.entities],
  );

  const onInvestorsRowSelectionChanged = useMemo(
    () =>
      makeOnRowSelectionChanged(
        form,
        "investors",
        values.investors,
        investorsRowSelectionState,
      ),
    [investorsRowSelectionState, form, values.investors],
  );

  const handleSubmit = useCallback(
    (
      onSubmit: (
        data: AllocationRuleEdgeListUpsertDataRequest,
      ) => Promise<void>,
    ) =>
      async (data: AllocationSetupFormData) => {
        const investors = selectedEntries(data.investors);
        return onSubmit({
          edges: [
            ...investors.map(([securityOwnershipId]) => ({
              source_allocation_rule_id: allocationRuleId,
              security_ownership_id: securityOwnershipId,
            })),
          ],
        });
      },
    [allocationRuleId],
  );

  const tableProps: AllocationEntitiesTableProps = {
    fundFamilyId,
    allocationRuleId,
    entityAllocationsData,
    currentInvestingEntityData,
    investorsRowSelectionState,
    entitiesRowSelectionState,
    numSelectedInvestors,
    numSelectedEntities,
    onInvestorsRowSelectionChanged,
    onEntitiesRowSelectionChanged,
  };

  // Not currently using the submit/forms for allocations
  return {
    form,
    handleSubmit,
    tableProps,
  } as const;
};
